import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { payoutService, PayoutBatch } from '../../services/payout.service'
import toast from 'react-hot-toast'

const STATUS_FILTERS = [
  'PENDING',
  'APPROVED',
  'PROCESSING',
  'COMPLETED',
  'PARTIALLY_COMPLETED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
] as const

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  SCHEDULED: 'bg-indigo-100 text-indigo-800',
  PROCESSING: 'bg-sky-100 text-sky-800',
  COMPLETED: 'bg-emerald-100 text-emerald-800',
  PARTIALLY_COMPLETED: 'bg-teal-100 text-teal-800',
  FAILED: 'bg-red-100 text-red-800',
  REJECTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-neutral-100 text-neutral-700',
}

const fmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const rm = (amount: string | number | null | undefined) =>
  `RM ${Number(amount ?? 0).toFixed(2)}`

export default function PayoutsPage() {
  const navigate = useNavigate()
  const [batches, setBatches] = useState<PayoutBatch[]>([])
  const [summary, setSummary] = useState<PayoutBatchesSummary | null>(null)
  const [filter, setFilter] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await payoutService.list(filter || undefined)
      setBatches(data.data)
      setSummary(data.summary)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load payouts')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    load()
  }, [load])

  const statusPill = (status: string) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
        STATUS_COLORS[status] ?? 'bg-neutral-100 text-neutral-700'
      }`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="mx-auto max-w-6xl px-6">
        <h1 className="text-2xl font-bold text-neutral-900">Payouts</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Transfer each payout offline from the company bank account, then mark the recipient as
          transferred. Batches complete when every recipient is paid.
        </p>

        {summary && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <p className="text-xs text-neutral-500">Outstanding (pending + approved)</p>
              <p className="mt-1 text-lg font-semibold text-neutral-900">{rm(summary.totalAmount)}</p>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <p className="text-xs text-neutral-500">Pending batches</p>
              <p className="mt-1 text-lg font-semibold text-amber-700">{summary.pendingBatches}</p>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <p className="text-xs text-neutral-500">Processing</p>
              <p className="mt-1 text-lg font-semibold text-sky-700">{summary.processingBatches}</p>
            </div>
            <div className="rounded-xl border border-neutral-200 bg-white p-4">
              <p className="text-xs text-neutral-500">Completed</p>
              <p className="mt-1 text-lg font-semibold text-emerald-700">{summary.completedBatches}</p>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              filter === '' ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 ring-1 ring-neutral-200'
            }`}
          >
            All ({batches.length})
          </button>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                filter === s ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 ring-1 ring-neutral-200'
              }`}
            >
              {s.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {loading ? (
            <p className="text-sm text-neutral-500">Loading…</p>
          ) : batches.length === 0 ? (
            <div className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
              No payout batches{filter ? ` with status ${filter.replace(/_/g, ' ')}` : ''}.
            </div>
          ) : (
            batches.map((batch) => (
              <button
                key={batch.id}
                onClick={() => navigate(`/admin/claims/payouts/${batch.id}`)}
                className="block w-full rounded-xl border border-neutral-200 bg-white p-4 text-left hover:border-neutral-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm font-semibold text-neutral-900">{batch.batchNumber}</p>
                    <p className="text-xs text-neutral-500">
                      {batch.name} · {batch.type.replace(/_/g, ' ')} · {batch.itemCount} item(s) ·{' '}
                      {fmt.format(new Date(batch.createdAt))}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-neutral-700">{rm(batch.totalAmount)}</span>
                    {statusPill(batch.status)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

interface PayoutBatchesSummary {
  totalAmount: number
  pendingBatches: number
  processingBatches: number
  completedBatches: number
}
